import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Search, Sparkles, ArrowRight, Star, Zap } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * पूछो Guruji — Homepage Hero Section
 * Premium entry-point widget. Displays:
 * - Hook headline
 * - Big search bar
 * - 6 most-popular question chips
 * - CTA to /poocho-guruji full page
 */
export default function PoochoGurujiHero() {
  const { lang } = useLang();
  const [popularQs, setPopularQs] = useState([]);
  const [customQ, setCustomQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/poocho-guruji/questions`).then(res => {
      const top = (res.data.questions || []).slice(0, 6);
      setPopularQs(top);
    }).catch(() => {});
  }, []);

  const navigateToAsk = (slug, custom) => {
    const params = new URLSearchParams();
    if (slug) params.set('q', slug);
    if (custom) params.set('custom', custom);
    window.location.href = `/poocho-guruji?${params.toString()}`;
  };

  return (
    <section className="relative py-16 sm:py-20 overflow-hidden" data-testid="poocho-guruji-hero">
      {/* Cinematic background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-950 via-purple-900/95 to-amber-950/30" />
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-gold/12 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-purple-500/15 blur-[140px] pointer-events-none" />

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto"
        >
          {/* Top badge */}
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/15 border border-gold/40 text-gold text-[11px] font-bold tracking-[0.2em] uppercase">
              <Zap className="w-3.5 h-3.5" />
              {lang === 'hi' ? 'नया · सबसे पहले यहीं पूछें' : 'NEW · Ask First Here'}
            </span>
          </div>

          {/* Big headline */}
          <h2 className="font-heading text-center text-4xl sm:text-5xl lg:text-6xl text-text-main leading-[1.1] tracking-tight mb-3">
            <span className="text-gold-gradient">पूछो Guruji</span>
          </h2>

          <p className="text-center text-text-main text-base sm:text-lg leading-relaxed mb-1">
            <span className="text-gold font-semibold">आचार्य Vivek जी</span> के 33 वर्षों का अनुभव
          </p>
          <p className="text-center text-text-muted text-sm sm:text-base mb-2">
            पराशरीय ज्योतिष ग्रंथों के साथ — मात्र <span className="text-gold font-bold text-lg">₹99</span> में उत्तर
          </p>

          <div className="divine-divider mx-auto max-w-xs my-6" />

          {/* Search bar */}
          <motion.form
            onSubmit={(e) => { e.preventDefault(); if (customQ.trim().length >= 10) navigateToAsk(null, customQ.trim()); }}
            initial={{ scale: 0.95, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative max-w-2xl mx-auto mb-8"
          >
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-gold/30 via-amber-400/30 to-gold/30 rounded-full blur-xl opacity-60 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center gap-2 bg-purple-950/80 backdrop-blur-xl border-2 border-gold/40 rounded-full px-3 py-2 shadow-2xl">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold/30 to-amber-700/30 flex items-center justify-center shrink-0">
                  <Search className="w-5 h-5 text-gold" />
                </div>
                <input
                  type="text"
                  value={customQ}
                  onChange={(e) => setCustomQ(e.target.value)}
                  placeholder={lang === 'hi' ? 'अपना प्रश्न यहाँ लिखें... जैसे "मेरी शादी कब होगी?"' : 'Type your question here...'}
                  className="flex-1 bg-transparent outline-none text-text-main placeholder:text-text-muted/60 text-base px-2"
                  data-testid="poocho-search-input"
                  maxLength={500}
                />
                <button
                  type="submit"
                  disabled={customQ.trim().length < 10}
                  className="px-5 sm:px-6 py-2.5 rounded-full bg-gradient-to-r from-gold via-amber-400 to-amber-500 text-purple-950 font-bold text-sm shadow-lg hover:scale-105 active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-1.5"
                  data-testid="poocho-search-submit"
                >
                  {lang === 'hi' ? 'पूछें' : 'Ask'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-center text-[11px] text-text-muted/60 mt-2">
              {lang === 'hi' ? 'या नीचे लोकप्रिय प्रश्नों में से चुनें' : 'Or pick from popular questions below'}
            </p>
          </motion.form>

          {/* Popular question chips */}
          <div className="mb-8">
            <p className="text-center text-[11px] uppercase tracking-[0.25em] text-gold/80 font-semibold mb-4">
              {lang === 'hi' ? '🔥 सबसे ज्यादा पूछे जाने वाले प्रश्न' : '🔥 Most Asked Questions'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl mx-auto">
              {popularQs.map((q, i) => (
                <motion.button
                  key={q.slug}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.05 }}
                  onClick={() => navigateToAsk(q.slug)}
                  className="group flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.04] border border-gold/15 hover:border-gold/50 hover:bg-white/[0.08] transition-all text-left"
                  data-testid={`poocho-chip-${q.slug}`}
                >
                  <span className="text-xl shrink-0">{q.icon}</span>
                  <span className="flex-1 text-text-main text-sm leading-tight">{q.question_hi}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-gold/60 group-hover:text-gold group-hover:translate-x-0.5 transition-all shrink-0" />
                </motion.button>
              ))}
            </div>
          </div>

          {/* Trust micro-bar */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-7 text-[11px] text-text-muted">
            <span className="flex items-center gap-1.5"><Star className="w-3 h-3 text-gold" /> Swiss Ephemeris</span>
            <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-gold" /> 12-factor पराशरीय</span>
            <span className="flex items-center gap-1.5">📜 शास्त्रीय श्लोक सहित</span>
            <span className="flex items-center gap-1.5">⚡ 60 सेकंड में उत्तर</span>
          </div>

          {/* Big CTA */}
          <div className="text-center">
            <Link
              to="/poocho-guruji"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-gold via-amber-400 to-amber-500 text-purple-950 font-bold text-base shadow-2xl hover:scale-105 active:scale-95 transition-transform"
              data-testid="poocho-guruji-explore-btn"
            >
              {lang === 'hi' ? 'सभी 100 प्रश्न देखें' : 'Explore All 100 Questions'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
